import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Scale, Search, Sparkles, ShieldAlert, Gavel, AlertTriangle,
  FileText, Loader2, Calendar, MapPin, BookOpen
} from "lucide-react";
import { toast } from "sonner";
import NotFoundCard from "../components/NotFoundCard";
import IdentityResultCard from "../components/IdentityResultCard";
import { fromCourtRecords } from "../lib/identityNormalizers";

const API = `${window.location.origin}/api/ecourts`;

const FLAG_COLORS = {
  critical: { bg: "rgba(220, 38, 38, 0.18)", border: "#dc2626", text: "#fca5a5" },
  high:     { bg: "rgba(239, 68, 68, 0.12)", border: "#ef4444", text: "#ef4444" },
  medium:   { bg: "rgba(245, 158, 11, 0.10)", border: "#f59e0b", text: "#f59e0b" },
  low:      { bg: "rgba(34, 197, 94, 0.10)",  border: "#22c55e", text: "#22c55e" },
};

const CASE_TYPE_COLORS = {
  Criminal: "#ef4444",
  Civil: "#2563eb",
  Writ: "#a855f7",
  Company: "#f59e0b",
};

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "10px", padding: "18px" }}>
      <div className="flex items-center justify-between mb-2">
        <div style={{ color: "#64748b", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px" }}>{label}</div>
        <Icon style={{ width: 16, height: 16, color }} />
      </div>
      <div style={{ color, fontSize: "28px", fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function CaseRow({ c }) {
  const typeColor = CASE_TYPE_COLORS[c.type] || "#94a3b8";
  return (
    <div style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "12px 14px", marginBottom: "8px" }}>
      <div className="flex items-center justify-between mb-1">
        <div style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 600 }}>{c.title}</div>
        <div className="flex items-center gap-2">
          <span style={{ background: `${typeColor}22`, color: typeColor, border: `1px solid ${typeColor}55`, fontSize: "10px", padding: "2px 8px", borderRadius: "4px", fontWeight: 700, letterSpacing: "0.4px" }}>{c.type}</span>
          <span style={{ background: c.status === "Pending" ? "rgba(245,158,11,0.15)" : "rgba(34,197,94,0.15)", color: c.status === "Pending" ? "#f59e0b" : "#22c55e", fontSize: "10px", padding: "2px 8px", borderRadius: "4px", fontWeight: 700 }}>{c.status}</span>
        </div>
      </div>
      <div style={{ color: "#94a3b8", fontSize: "11px", marginBottom: "4px" }}>
        {c.court} · {c.state} · CNR: <span style={{ fontFamily: "ui-monospace, monospace", color: "#cbd5e1" }}>{c.cnr}</span>
      </div>
      <div style={{ color: "#64748b", fontSize: "11px" }}>
        Act: <strong style={{ color: "#cbd5e1" }}>{c.act}</strong> §{c.section} · Filed {c.filing_date} · Next: {c.next_hearing || "—"}
      </div>
      {c.last_order && (
        <div style={{ marginTop: "6px", padding: "6px 10px", background: "#0d1117", borderRadius: "4px", color: "#94a3b8", fontSize: "11px", fontStyle: "italic", borderLeft: "2px solid #2563eb" }}>
          {c.last_order}
        </div>
      )}
    </div>
  );
}

export function ECourtsLookupPanel({ initialName = null, compact = false }) {
  const [searchName, setSearchName] = useState(initialName || "");
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [risk, setRisk] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({ total: 0, with_pmla: 0, with_criminal: 0, cases_created: 0 });

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/lookups`, { withCredentials: true });
      setHistory(data.lookups || []);
      setStats(data.stats || { total: 0, with_pmla: 0, with_criminal: 0, cases_created: 0 });
    } catch (err) { console.error("[ECourts] Failed to load history:", err); }
  }, []);

  useEffect(() => { if (!compact) loadHistory(); }, [loadHistory, compact]);

  const runLookup = async () => {
    if (!searchName.trim()) {
      toast.error("Enter a name to search");
      return;
    }
    setLooking(true); setResult(null); setRisk(null);
    try {
      const { data } = await axios.post(
        `${API}/lookup`,
        { search_name: searchName },
        { withCredentials: true }
      );
      setResult(data.result);
      setRisk(data.risk);
      const total = data.result?.total_cases || 0;
      toast.success(total > 0 ? `${total} case(s) found` : "No cases on record");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lookup failed");
    } finally {
      setLooking(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/save`,
        { search_name: searchName, result, link_to_customer_id: null },
        { withCredentials: true }
      );
      if (data.created_case) {
        toast.success(`Saved. Case ${data.created_case.case_id} auto-created.`);
      } else {
        toast.success("eCourts lookup saved.");
      }
      setSearchName(""); setResult(null); setRisk(null);
      if (!compact) loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="ecourts-panel">
      {!compact && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "24px" }}>
          <StatCard label="Total Lookups" value={stats.total} color="#2563eb" icon={Scale} />
          <StatCard label="With PMLA/FEMA" value={stats.with_pmla} color="#fca5a5" icon={ShieldAlert} />
          <StatCard label="With Criminal" value={stats.with_criminal} color="#ef4444" icon={Gavel} />
          <StatCard label="Cases Created" value={stats.cases_created} color="#f59e0b" icon={FileText} />
        </div>
      )}

      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px", padding: compact ? "16px" : "24px" }}>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles style={{ width: 18, height: 18, color: "#2563eb" }} />
          <h2 style={{ fontSize: compact ? "14px" : "16px", fontWeight: 700, margin: 0, color: "#f1f5f9" }}>
            {compact ? "Court Record Quick Lookup" : "Search Indian Court Records (NJDG)"}
          </h2>
        </div>

        <div className="flex gap-3">
          <input
            type="text"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runLookup()}
            placeholder="Person or Company name (e.g., 'Mehul Choksi' or 'Reliance Industries')"
            data-testid="search-name-input"
            style={{ flex: 1, background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "10px 14px", color: "#f1f5f9", fontSize: "13px" }}
          />
          <button
            onClick={runLookup}
            disabled={looking}
            data-testid="ecourts-lookup-btn"
            style={{
              background: looking ? "#1e2530" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
              border: "none", borderRadius: "8px", color: "white", padding: "10px 22px",
              fontWeight: 600, fontSize: "13px", cursor: looking ? "wait" : "pointer",
              display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap",
            }}
          >
            {looking ? (<><Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Searching...</>) : (<><Search style={{ width: 14, height: 14 }} /> Search NJDG</>)}
          </button>
        </div>
        <div style={{ color: "#64748b", fontSize: "11px", marginTop: "8px" }}>
          Powered by Claude Sonnet 4.5 · Source: eCourts NJDG
        </div>
      </div>

      {/* Result */}
      {result && result.found === false && <NotFoundCard result={result} />}
      {result && result.found !== false && (
        <>
        <div style={{ marginTop: "20px" }}>
          <IdentityResultCard person={fromCourtRecords(result, result.search_name)} />
        </div>
        <div
          style={{ background: "#0d1117", border: `1px solid ${result.total_cases > 0 ? "#ef4444" : "#22c55e"}`, borderRadius: "12px", padding: "24px", marginTop: "20px" }}
          data-testid="ecourts-result-card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Scale style={{ color: result.total_cases > 0 ? "#ef4444" : "#22c55e", width: 22, height: 22 }} />
              <h3 style={{ color: "#f1f5f9", fontSize: "18px", fontWeight: 700, margin: 0 }}>{result.search_name}</h3>
              <span style={{ background: result.total_cases > 0 ? "rgba(239,68,68,0.15)" : "rgba(34,197,94,0.15)", color: result.total_cases > 0 ? "#ef4444" : "#22c55e", borderRadius: "6px", padding: "4px 10px", fontSize: "11px", fontWeight: 700 }}>
                {result.total_cases} CASE{result.total_cases === 1 ? "" : "S"}
              </span>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-ecourts-btn"
              style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", borderRadius: "6px", color: "white", padding: "8px 16px", fontWeight: 600, fontSize: "12px", cursor: saving ? "wait" : "pointer", display: "flex", alignItems: "center", gap: "6px" }}
            >
              {saving ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> : <FileText style={{ width: 12, height: 12 }} />}
              {saving ? "Saving..." : "Save & Flag"}
            </button>
          </div>

          {/* Counts grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "8px", marginBottom: "16px" }}>
            <MiniStat label="Active" value={result.active_cases} color="#f59e0b" />
            <MiniStat label="Disposed" value={result.disposed_cases} color="#22c55e" />
            <MiniStat label="Criminal" value={result.criminal_cases} color="#ef4444" />
            <MiniStat label="Civil" value={result.civil_cases} color="#2563eb" />
            <MiniStat label="PMLA/FEMA" value={result.pmla_fema_cases} color="#fca5a5" highlight={result.pmla_fema_cases > 0} />
            <MiniStat label="Total" value={result.total_cases} color="#f1f5f9" />
          </div>

          {/* Risk flags */}
          {risk?.flags?.length > 0 && (
            <div style={{ marginBottom: "16px" }}>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
                Risk Flags ({risk.flags.length}) · +{risk.risk_score_added} risk points
              </div>
              {risk.flags.map((f, idx) => {
                const c = FLAG_COLORS[f.severity] || FLAG_COLORS.medium;
                return (
                  <div key={idx} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: "6px", padding: "8px 12px", marginBottom: "6px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <AlertTriangle style={{ width: 14, height: 14, color: c.text }} />
                    <span style={{ color: c.text, fontSize: "12px", fontWeight: 600 }}>{f.label}</span>
                    <span style={{ color: c.text, fontSize: "9px", marginLeft: "auto", textTransform: "uppercase", letterSpacing: "1px" }}>{f.severity}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Case list */}
          {result.cases?.length > 0 && (
            <div>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
                Case Details ({result.cases.length})
              </div>
              {result.cases.map((c, idx) => <CaseRow key={idx} c={c} />)}
            </div>
          )}

          {result.total_cases === 0 && (
            <div style={{ color: "#22c55e", fontSize: "13px", textAlign: "center", padding: "16px" }}>
              ✓ Clean record — no cases found in eCourts NJDG.
            </div>
          )}

          <div style={{ marginTop: "12px", color: "#475569", fontSize: "10px", textAlign: "right" }}>
            AI: {result.ai_mode || "claude-sonnet-4.5"} · Source: {result.source}
          </div>
        </div>
        </>
      )}

      {/* History */}
      {!compact && (
        <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px", padding: "24px", marginTop: "24px" }}>
          <h2 style={{ fontSize: "16px", fontWeight: 700, margin: "0 0 16px", color: "#f1f5f9" }}>Recent Lookups ({history.length})</h2>
          {history.length === 0 ? (
            <div style={{ color: "#64748b", fontSize: "13px", textAlign: "center", padding: "32px" }}>
              No lookups yet. Try "Mehul Choksi" ya "Vijay Mallya" se multi-case fraudster output dekho.
            </div>
          ) : (
            <div style={{ display: "grid", gap: "8px" }}>
              {history.map((l) => {
                const r = l.result || {};
                return (
                  <div key={l.id} style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "12px 16px", display: "grid", gridTemplateColumns: "1fr auto auto auto auto", alignItems: "center", gap: "16px" }}>
                    <div style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 600 }}>{l.search_name}</div>
                    {r.pmla_fema_cases > 0 && <span style={{ background: "rgba(220, 38, 38, 0.18)", color: "#fca5a5", borderRadius: "4px", padding: "2px 8px", fontSize: "10px", fontWeight: 700 }}>PMLA · {r.pmla_fema_cases}</span>}
                    <div style={{ color: "#ef4444", fontSize: "11px" }}>{r.criminal_cases || 0} criminal</div>
                    <div style={{ color: "#94a3b8", fontSize: "11px" }}>{r.total_cases || 0} total</div>
                    <div style={{ color: "#64748b", fontSize: "10px" }}>{new Date(l.created_at).toLocaleDateString()}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, color, highlight }) {
  return (
    <div style={{ background: highlight ? "rgba(252, 165, 165, 0.10)" : "#080c12", border: highlight ? "1px solid #fca5a5" : "1px solid #1e2530", borderRadius: "6px", padding: "10px 8px", textAlign: "center" }}>
      <div style={{ color: "#64748b", fontSize: "9px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px" }}>{label}</div>
      <div style={{ color, fontSize: "20px", fontWeight: 700, lineHeight: 1.2 }}>{value || 0}</div>
    </div>
  );
}

export default function ECourtsPage() {
  return (
    <div data-testid="ecourts-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center gap-3 mb-2">
        <Scale style={{ width: 26, height: 26, color: "#2563eb" }} />
        <h1 style={{ fontSize: "26px", fontWeight: 700, margin: 0, color: "#f1f5f9" }} data-testid="page-title">Court Records (eCourts NJDG)</h1>
      </div>
      <p style={{ color: "#94a3b8", fontSize: "13px", marginBottom: "24px" }}>
        Search Indian court records via NJDG. Auto-flags PMLA/FEMA, criminal cases &amp; heavy litigation profiles.
      </p>
      <ECourtsLookupPanel />
    </div>
  );
}
